// =============================================================================
// Socket.IO Terminal Component - Class-based Implementation
// =============================================================================

import React, { Component } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { io } from 'socket.io-client';
import { API_CONFIG } from '../config/api.js';
import 'xterm/css/xterm.css';

// 全局实例管理 - 确保只有一个终端实例
let globalTerminalInstance = null;
let globalSocketInstance = null;
let globalFitAddon = null;
let instanceCounter = 0;

// 移除所有会话管理代码，简化为独立终端

// 清理过期会话的函数
const cleanupExpiredSessions = () => {
  const now = new Date();
  const maxAge = 30 * 60 * 1000; // 30 分钟

  for (const [key, session] of terminalSessions.entries()) {
    const sessionTime = new Date(session.timestamp);
    if (now - sessionTime > maxAge) {
      console.log(`🧹 Cleaning up expired session: ${key}`);
      try {
        if (session.terminal) {
          session.terminal.dispose();
        }
        if (session.fitAddon) {
          session.fitAddon.dispose();
        }
      } catch (error) {
        console.error('❌ Error disposing expired session:', error);
      }
      terminalSessions.delete(key);
    }
  }
};

// 定期清理过期会话
setInterval(cleanupExpiredSessions, 5 * 60 * 1000); // 每 5 分钟清理一次

class SocketIOTerminal extends Component {
  constructor(props) {
    super(props);

    this.instanceId = ++instanceCounter;
    console.log(`🚀 SocketIOTerminal instance ${this.instanceId} constructor: workspace=${props.workspace?.id}, terminalId=${props.terminalId}, hasOnTerminalCreated=${!!props.onTerminalCreated}, timestamp=${new Date().toISOString()}`);

    this.state = {
      isConnected: false,
      isConnecting: false,
      connectionStatus: 'disconnected',
      domReady: false
    };

    this.terminalRef = null;
    this.terminal = null;
    this.fitAddon = null;
    this.socket = null;
    this.mounted = false;
    this.dataEventCount = 0; // 跟踪数据事件数量

    // 绑定方法
    this.setTerminalRef = this.setTerminalRef.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleReconnect = this.handleReconnect.bind(this);
    this.getSessionKey = this.getSessionKey.bind(this);
  }

  componentDidMount() {
    console.log(`🎬 SocketIOTerminal instance ${this.instanceId} componentDidMount: workspace=${this.props.workspace?.id}, terminalId=${this.props.terminalId}, timestamp=${new Date().toISOString()}`);
    this.mounted = true;

    // 简化逻辑：每个终端都是独立的
    console.log(`🔄 Instance ${this.instanceId} initializing independent terminal for ${this.props.terminalId}`);

    // 设置当前终端ID
    this.currentTerminalId = this.props.terminalId;

    // 会话复用逻辑已经在上面处理了，这里直接初始化新终端

    // 初始化终端和连接
    this.initializeTerminal();
    this.initializeSocket();

    // 添加窗口大小调整监听器
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    console.log(`🎬 SocketIOTerminal instance ${this.instanceId} componentWillUnmount: workspace=${this.props.workspace?.id}, terminalId=${this.props.terminalId}, timestamp=${new Date().toISOString()}`);
    this.mounted = false;

    // 简化：直接清理，不保存会话
    this.cleanup();
  }

  componentDidUpdate(prevProps) {
    // 检查回调函数更新
    if (prevProps.onTerminalCreated !== this.props.onTerminalCreated) {
      console.log(`🔄 Instance ${this.instanceId} callback updated for ${this.props.terminalId?.slice(-8)}`);
    }

    // 检查终端 ID 或工作空间变化
    if (
      prevProps.terminalId !== this.props.terminalId ||
      prevProps.workspace?.id !== this.props.workspace?.id
    ) {
      console.log(`🔄 Instance ${this.instanceId} terminal/workspace changed:`, {
        prevTerminalId: prevProps.terminalId,
        currentTerminalId: this.props.terminalId,
        prevWorkspaceId: prevProps.workspace?.id,
        currentWorkspaceId: this.props.workspace?.id
      });

      // 更新当前终端 ID
      this.currentTerminalId = this.props.terminalId;

      // 如果 Socket.IO 已连接，直接重新连接终端
      if (this.socket && this.socket.connected) {
        this.connectToTerminal();
      } else {
        // 否则重新初始化整个连接
        this.initializeSocket();
      }
    }
  }

  initializeTerminal() {
    console.log(`🖥️ ===== TERMINAL INITIALIZATION STARTED (Instance ${this.instanceId}) =====`);
    console.log(`🖥️ Terminal initialization called: terminalRefExists=${!!this.terminalRef}, terminalExists=${!!this.terminal}, terminalId=${this.props.terminalId}, timestamp=${new Date().toISOString()}`);

    if (!this.terminalRef) {
      console.log(`�️ Instance ${this.instanceId} skipping terminal initialization: No DOM element`);
      return;
    }

    // 检查是否已经有终端在这个 DOM 元素中
    if (this.terminalRef.children.length > 0) {
      console.log(`�️ Instance ${this.instanceId} terminal already exists in DOM element (children: ${this.terminalRef.children.length}), skipping initialization`);
      return;
    }

    // 清理可能存在的 xterm 元素
    const existingXtermElements = this.terminalRef.querySelectorAll('.xterm');
    if (existingXtermElements.length > 0) {
      console.log(`🧹 Instance ${this.instanceId} found ${existingXtermElements.length} existing xterm elements, cleaning up...`);
      existingXtermElements.forEach(element => {
        element.remove();
      });
    }

    console.log(`🖥️ Instance ${this.instanceId} creating terminal instance...`);
    try {
      // Create terminal instance - 强制暗黑主题
      this.terminal = new Terminal({
        cursorBlink: true,
        fontSize: 14,
        fontFamily: 'Consolas, Monaco, Menlo, "Ubuntu Mono", monospace',
        theme: {
          background: '#0d1117',      // GitHub 暗黑主题背景
          foreground: '#e6edf3',      // 主要文字颜色
          cursor: '#f0f6fc',          // 光标颜色
          cursorAccent: '#0d1117',    // 光标背景
          selection: '#264f78',       // 选择背景
          selectionForeground: '#ffffff', // 选择文字
          black: '#484f58',           // 黑色
          red: '#ff7b72',             // 红色
          green: '#3fb950',           // 绿色
          yellow: '#d29922',          // 黄色
          blue: '#58a6ff',            // 蓝色
          magenta: '#bc8cff',         // 洋红
          cyan: '#39c5cf',            // 青色
          white: '#b1bac4',           // 白色
          brightBlack: '#6e7681',     // 亮黑
          brightRed: '#ffa198',       // 亮红
          brightGreen: '#56d364',     // 亮绿
          brightYellow: '#e3b341',    // 亮黄
          brightBlue: '#79c0ff',      // 亮蓝
          brightMagenta: '#d2a8ff',   // 亮洋红
          brightCyan: '#56d4dd',      // 亮青
          brightWhite: '#f0f6fc'      // 亮白
        },
        allowTransparency: false,     // 禁用透明度，确保暗黑背景
        scrollback: 1000,
        convertEol: true,
        windowsMode: false,           // 确保 Unix 模式
        macOptionIsMeta: true,        // Mac 选项键作为 Meta
        rightClickSelectsWord: true,  // 右键选择单词
        fastScrollModifier: 'shift'   // Shift 快速滚动
      });

      // 不再设置全局实例，每个终端独立管理
      console.log(`🖥️ Instance ${this.instanceId} terminal instance created successfully:`, this.terminal);

      console.log(`🖥️ Instance ${this.instanceId} loading fit addon...`);
      // Create fit addon
      this.fitAddon = new FitAddon();
      this.terminal.loadAddon(this.fitAddon);
      console.log(`🖥️ Instance ${this.instanceId} fit addon loaded successfully:`, this.fitAddon);

      console.log(`🖥️ Instance ${this.instanceId} opening terminal in DOM element...`);

      // Wait for DOM to be fully stable before opening terminal
      setTimeout(() => {
        if (!this.mounted) return;

        try {
          console.log(`🖥️ Instance ${this.instanceId} opening terminal in DOM...`);
          this.terminal.open(this.terminalRef);
          console.log(`🖥️ Instance ${this.instanceId} terminal opened in DOM`);

          // Wait additional time for xterm.js internal setup
          setTimeout(() => {
            if (!this.mounted) return;

            try {
              console.log(`🖥️ Instance ${this.instanceId} fitting terminal...`);
              this.fitAddon.fit();
              console.log(`🖥️ Instance ${this.instanceId} terminal fitted`);

              console.log(`🖥️ Instance ${this.instanceId} terminal initialized successfully:`, {
                cols: this.terminal.cols,
                rows: this.terminal.rows,
                element: !!this.terminal.element,
                elementTagName: this.terminal.element?.tagName,
                elementParent: !!this.terminal.element?.parentElement
              });

              // 终端初始化完成，不再写入测试信息
              console.log(`🖥️ Instance ${this.instanceId} terminal ready for input`);
            } catch (fitError) {
              console.error(`❌ Instance ${this.instanceId} error fitting terminal:`, fitError);
            }
          }, 200);
        } catch (openError) {
          console.error(`❌ Instance ${this.instanceId} error opening terminal:`, openError);
          return;
        }
      }, 100);

    } catch (error) {
      console.error(`❌ Instance ${this.instanceId} error during terminal initialization:`, error);
      console.error('❌ Error stack:', error.stack);
    }

    // Handle terminal input
    this.terminal.onData((data) => {
      if (this.socket && this.socket.connected) {
        this.socket.emit('terminal:input', {
          terminalId: this.props.terminalId,
          input: data
        });
      }
    });

    // Handle terminal resize
    this.terminal.onResize(({ cols, rows }) => {
      if (this.socket && this.socket.connected) {
        this.socket.emit('terminal:resize', {
          terminalId: this.props.terminalId,
          cols,
          rows
        });
      }
    });
  }

  initializeSocket() {
    console.log(`🔌 ===== SOCKET.IO INITIALIZATION STARTED (Instance ${this.instanceId}) =====`);
    console.log(`🚀 Instance ${this.instanceId} Socket.IO initialization:`, {
      terminalId: this.props.terminalId,
      workspaceId: this.props.workspace?.id,
      terminalExists: !!this.terminal,
      socketExists: !!this.socket,
      instanceId: this.instanceId,
      timestamp: new Date().toISOString()
    });

    // 获取 token - 优先从 props，然后从 localStorage
    const token = this.props.token || localStorage.getItem('auth_token');
    if (!token) {
      console.error(`❌ Instance ${this.instanceId} no authentication token found`);
      this.terminal?.writeln('\r\n❌ No authentication token found. Please provide token via props or localStorage.');
      return;
    }

    console.log(`🔌 Instance ${this.instanceId} setting connection state to connecting...`);
    this.setState({
      isConnecting: true,
      connectionStatus: 'connecting'
    });

    // Create Socket.IO connection to backend server
    // 使用配置的后端 URL，支持开发和生产环境
    const backendUrl = API_CONFIG.WS_BASE_URL;
    console.log(`🔌 Instance ${this.instanceId} attempting Socket.IO connection to:`, `${backendUrl}/terminal`);

    try {
      this.socket = io(`${backendUrl}/terminal`, {
        auth: { token },
        transports: ['polling'], // 暂时只使用 polling，避免 websocket 问题
        upgrade: false, // 暂时禁用升级
        rememberUpgrade: false,
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 10, // 增加重连次数
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000, // 增加最大重连延迟
        timeout: 30000, // 增加超时时间
        forceNew: false, // 复用连接
        multiplex: true // 允许多路复用
      });

      console.log(`🔌 Instance ${this.instanceId} Socket.IO instance created (independent connection)`);
      this.setupSocketEventHandlers();
    } catch (error) {
      console.error(`❌ Instance ${this.instanceId} error creating Socket.IO instance:`, error);
      this.setState({
        isConnecting: false,
        connectionStatus: 'error'
      });
    }
  }

  setupSocketEventHandlers() {
    if (!this.socket) return;

    // Connection event handlers
    this.socket.on('connect', () => {
      console.log(`🔌 Instance ${this.instanceId} Socket.IO connected:`, this.socket.id);

      if (!this.mounted) return;

      this.setState({
        isConnected: true,
        isConnecting: false,
        connectionStatus: 'connected'
      });

      console.log(`� Instance ${this.instanceId} writing connection message to terminal...`);
      // 不再向终端写入连接信息，让 shell 自然显示

      // Request terminal connection - 总是尝试连接到已存在的终端
      const connectData = {
        terminalId: this.props.terminalId,
        workspaceId: this.props.workspace.id,
        action: 'connect', // 总是尝试连接，后端会处理创建逻辑
        cols: this.terminal?.cols || 80,
        rows: this.terminal?.rows || 24,
        requestHistory: true // 请求历史记录
      };
      console.log(`� Instance ${this.instanceId} sending terminal:connect event:`, connectData);

      try {
        this.socket.emit('terminal:connect', connectData);
        console.log(`✅ Instance ${this.instanceId} terminal:connect event sent successfully`);
      } catch (error) {
        console.error(`❌ Instance ${this.instanceId} error sending terminal:connect event:`, error);
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log(`🔌 Instance ${this.instanceId} Socket.IO disconnected:`, reason);
      if (!this.mounted) return;

      this.setState({
        isConnected: false,
        connectionStatus: 'disconnected'
      });
      this.terminal?.writeln(`\r\n❌ Disconnected: ${reason}`);
    });

    this.socket.on('connect_error', (error) => {
      console.error(`❌ Instance ${this.instanceId} Socket.IO connection error:`, error);
      if (!this.mounted) return;

      // 检查是否是网络错误，如果是则尝试重连
      if (error.message.includes('xhr poll error') || error.message.includes('timeout')) {
        console.log(`🔄 Instance ${this.instanceId} Network error detected, will retry...`);
        this.setState({ connectionStatus: 'reconnecting' });
        this.terminal?.writeln(`\r\n🔄 Network error, reconnecting...`);
      } else {
        this.setState({
          isConnecting: false,
          connectionStatus: 'error'
        });
        this.terminal?.writeln(`\r\n❌ Connection error: ${error.message}`);
      }
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`🔄 Instance ${this.instanceId} Socket.IO reconnected after ${attemptNumber} attempts`);
      this.terminal?.writeln(`\r\n🔄 Reconnected (attempt ${attemptNumber})`);
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`� Instance ${this.instanceId} Socket.IO reconnection attempt:`, attemptNumber);
      if (!this.mounted) return;

      this.setState({ connectionStatus: 'reconnecting' });
      this.terminal?.writeln(`\r\n🔄 Reconnecting... (attempt ${attemptNumber})`);
    });

    this.socket.on('reconnect_failed', () => {
      console.error(`❌ Instance ${this.instanceId} Socket.IO reconnection failed`);
      if (!this.mounted) return;

      this.setState({ connectionStatus: 'failed' });
      this.terminal?.writeln('\r\n❌ Reconnection failed. Click the reconnect button or refresh the page.');
    // Unified handler to write data into xterm
    this.handleTerminalData = (data) => {
      this.dataEventCount++;
      console.log(`📨 Instance ${this.instanceId} received terminal data (event #${this.dataEventCount}):`, {
        type: typeof data,
        dataLength: data?.length || 0,
        terminalExists: !!this.terminal,
        mounted: this.mounted,
        currentTerminalId: this.props.terminalId || this.currentTerminalId,
        dataPreview: data?.substring ? data.substring(0, 100) : data,
        dataBytes: data ? Array.from(data).map(c => c.charCodeAt(0)) : []
      });

      if (!this.mounted || !this.terminal) return;
      try {
        this.terminal.write(data);
      } catch (e) {
        console.error(`❌ Instance ${this.instanceId} failed to write data:`, e);
      }
    };
    });

    // 添加调试信息
    console.log(`🔧 Instance ${this.instanceId} setting up event listeners, socket connected: ${this.socket.connected}`);

    // Terminal event handlers (support both event names)
    this.socket.on('terminal:data', (data) => {
      // Fallthrough to unified handler
      this.handleTerminalData?.(data);
    });

    this.socket.on('terminal:terminal:data', (data) => {
      this.dataEventCount++;
      console.log(`� Instance ${this.instanceId} received terminal data (event #${this.dataEventCount}):`, {
        type: typeof data,
        dataLength: data?.length || 0,
        terminalExists: !!this.terminal,
        mounted: this.mounted,
        currentTerminalId: this.props.terminalId || this.currentTerminalId,
        dataPreview: data?.substring ? data.substring(0, 100) : data,
        dataBytes: data ? Array.from(data).map(c => c.charCodeAt(0)) : []
      });

      // 检查组件是否已卸载
      if (!this.mounted) {
        console.log(`🚫 Instance ${this.instanceId} ignoring data: component unmounted`);
        return;
      }

      if (!this.terminal) {
        console.warn(`⚠️ Instance ${this.instanceId} cannot write data: terminal not initialized`);
        return;
      }

      try {
        this.terminal.write(data);
        console.log(`📨 Instance ${this.instanceId} data written successfully - wrote ${data.length} chars`);
      } catch (error) {
        console.error(`❌ Instance ${this.instanceId} error writing data to terminal:`, error);
      }
    });

    this.socket.on('terminal:created', (data) => {
      console.log(`✅ Instance ${this.instanceId} terminal created:`, data);
      // 不再向终端写入创建信息
      if (!this.mounted) return;

      // 更新当前终端 ID
      this.currentTerminalId = data.terminalId;

      // 对于新创建的终端（没有传入 terminalId 的情况），调用回调函数使其被选中
      if (this.props.onTerminalCreated && (!this.props.terminalId || this.props.terminalId === 'undefined')) {
        console.log(`🔌 Instance ${this.instanceId} calling onTerminalCreated for new terminal:`, data);

        // 确保传递完整的终端对象，包含所有必要的属性
        const terminalData = {
          terminalId: data.terminalId,
          id: data.terminalId,
          workspaceId: this.props.workspace?.id,
          name: `Terminal ${data.terminalId.slice(-8)}`,
          status: 'running',
          cols: data.cols || 80,
          rows: data.rows || 24,
          shell: data.shell || '/bin/bash',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          ...data // 包含后端返回的其他数据
        };

        console.log(`🔌 Instance ${this.instanceId} terminal data for callback:`, terminalData);
        this.props.onTerminalCreated(terminalData);
      }
    });

    this.socket.on('terminal:connected', (data) => {
      console.log(`✅ Instance ${this.instanceId} terminal connected:`, data);
      // 不再向终端写入连接信息
      if (!this.mounted) return;

      // 更新当前终端 ID
      this.currentTerminalId = data.terminalId;

      // 添加调试：检查是否是旧终端重新连接
      if (data.isNew === false) {
        console.log(`🔄 Instance ${this.instanceId} reconnected to existing terminal ${data.terminalId}`);
      } else {
        console.log(`🆕 Instance ${this.instanceId} connected to new terminal ${data.terminalId}`);
      }

      this.setState({ connectionStatus: 'connected' });
    });

    this.socket.on('terminal:error', (error) => {
      console.error(`❌ Instance ${this.instanceId} terminal error:`, error);
      if (!this.mounted) return;

      this.setState({ connectionStatus: 'error' });

      // 如果是 "Terminal not found" 错误，尝试创建新终端
      if (error.error && error.error.includes('Terminal not found')) {
        this.terminal?.writeln(`\r\n❌ Terminal not found, creating new terminal...`);
        this.handleTerminalNotFound();
      } else {
        this.terminal?.writeln(`\r\n❌ Terminal error: ${error.error || error.message || 'Unknown error'}`);
      }
    });
  }

  cleanup() {
    console.log(`🧹 Instance ${this.instanceId} cleanup called`);

    // 移除窗口大小调整监听器
    window.removeEventListener('resize', this.handleResize);

    // 简化：直接清理所有资源
    console.log(`🧹 Instance ${this.instanceId} cleaning up all resources`);

    // 清理终端实例
    if (this.terminal) {
      try {
        this.terminal.dispose();
      } catch (error) {
        console.error('❌ Error disposing terminal:', error);
      }
    }

    // 清理 FitAddon
    if (this.fitAddon) {
      try {
        this.fitAddon.dispose();
      } catch (error) {
        console.error('❌ Error disposing fit addon:', error);
      }
    }

    // 清理 Socket.IO 连接
    if (this.socket) {
      console.log(`🧹 Instance ${this.instanceId} disconnecting socket`);
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }

    this.terminal = null;
    this.fitAddon = null;
    this.socket = null;

    // Clean up any remaining xterm DOM elements in this instance's container
    if (this.terminalRef) {
      const xtermElements = this.terminalRef.querySelectorAll('.xterm');
      if (xtermElements.length > 0) {
        console.log(`🧹 Instance ${this.instanceId} cleaning up ${xtermElements.length} remaining xterm DOM elements`);
        xtermElements.forEach(element => {
          element.remove();
        });
      }
    }
  }

  setTerminalRef(el) {
    console.log(`🎨 Instance ${this.instanceId} terminal ref callback called:`, {
      element: el,
      tagName: el?.tagName,
      className: el?.className,
      offsetWidth: el?.offsetWidth,
      offsetHeight: el?.offsetHeight,
      isConnected: el?.isConnected,
      parentElement: !!el?.parentElement,
      previousRef: this.terminalRef,
      timestamp: new Date().toISOString()
    });

    this.terminalRef = el;

    // 如果 DOM 元素准备好了且还没有初始化终端，则初始化
    if (el && !this.terminal && this.mounted) {
      console.log(`🎨 Instance ${this.instanceId} DOM ready, initializing terminal...`);
      setTimeout(() => {
        if (this.mounted && this.terminalRef) {
          this.initializeTerminal();
        }
      }, 100);
    }
  }

  handleResize() {
    if (this.fitAddon && this.terminal) {
      this.fitAddon.fit();
    }
  }

  connectToTerminal() {
    if (!this.socket || !this.socket.connected) {
      console.log(`❌ Instance ${this.instanceId} cannot connect to terminal: socket not connected`);
      return;
    }

    const terminalId = this.props.terminalId || this.currentTerminalId;
    if (!terminalId) {
      console.log(`❌ Instance ${this.instanceId} cannot connect to terminal: no terminalId`);
      return;
    }

    const connectData = {
      terminalId: terminalId,
      workspaceId: this.props.workspace?.id,
      action: 'connect',
      cols: this.terminal?.cols || 80,
      rows: this.terminal?.rows || 24
    };

    console.log(`🔄 Instance ${this.instanceId} connecting to terminal:`, connectData);

    try {
      this.socket.emit('terminal:connect', connectData);
    } catch (error) {
      console.error(`❌ Instance ${this.instanceId} error connecting to terminal:`, error);
    }
  }

  isNewTerminal() {
    // 检查是否是新创建的终端（没有会话历史）
    const sessionKey = this.getSessionKey();
    const existingSession = terminalSessions.get(sessionKey);
    return !existingSession;
  }

  triggerOnTerminalCreated() {
    if (!this.props.onTerminalCreated || !this.props.terminalId) {
      return;
    }

    const terminalData = {
      terminalId: this.props.terminalId,
      id: this.props.terminalId,
      workspaceId: this.props.workspace?.id,
      name: `Terminal ${this.props.terminalId.slice(-8)}`,
      status: 'running',
      cols: this.terminal?.cols || 80,
      rows: this.terminal?.rows || 24,
      shell: '/bin/bash',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    console.log(`🔌 Instance ${this.instanceId} calling onTerminalCreated for terminal:`, terminalData);
    this.props.onTerminalCreated(terminalData);
  }

  getSessionKey() {
    // 创建基于工作空间和终端 ID 的会话键
    const workspaceId = this.props.workspace?.id;
    const terminalId = this.props.terminalId || this.currentTerminalId;

    if (terminalId) {
      return `terminal-${workspaceId}-${terminalId}`;
    } else {
      // 对于新终端，使用实例 ID 确保唯一性
      return `new-terminal-${workspaceId}-${this.instanceId}`;
    }
  }

  handleReconnect() {
    console.log(`🔄 Instance ${this.instanceId} manual reconnection triggered`);

    // 清理现有连接
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      globalSocketInstance = null;
    }

    // 重置状态
    this.setState({
      isConnected: false,
      isConnecting: true,
      connectionStatus: 'connecting'
    });

    // 显示重连消息
    this.terminal?.writeln('\r\n🔄 Manual reconnection initiated...');

    // 延迟一秒后重新初始化连接
    setTimeout(() => {
      if (this.mounted) {
        this.initializeSocket();
      }
    }, 1000);
  }

  handleTerminalNotFound() {
    console.log(`🔄 Instance ${this.instanceId} handling terminal not found - creating new terminal`);

    // 重置状态
    this.setState({
      isConnected: false,
      isConnecting: true,
      connectionStatus: 'connecting'
    });

    // 清除当前的 terminalId，强制创建新终端
    this.currentTerminalId = null;

    // 延迟一秒后重新连接（这次会创建新终端）
    setTimeout(() => {
      if (this.mounted && this.socket) {
        const connectData = {
          terminalId: undefined, // 不传递 terminalId，强制创建新终端
          workspaceId: this.props.workspace.id,
          action: 'create', // 强制创建新终端
          cols: this.terminal?.cols || 80,
          rows: this.terminal?.rows || 24
        };

        console.log(`🔄 Instance ${this.instanceId} creating new terminal after not found:`, connectData);
        this.socket.emit('terminal:connect', connectData);
      }
    }, 1000);
  }

  getStatusColor() {
    switch (this.state.connectionStatus) {
      case 'connected': return 'text-green-500';
      case 'connecting':
      case 'reconnecting': return 'text-yellow-500';
      case 'error':
      case 'failed': return 'text-red-500';
      default: return 'text-gray-500';
    }
  }

  getStatusText() {
    switch (this.state.connectionStatus) {
      case 'connected': return 'Connected';
      case 'connecting': return 'Connecting...';
      case 'reconnecting': return 'Reconnecting...';
      case 'error': return 'Connection Error';
      case 'failed': return 'Connection Failed';
      default: return 'Disconnected';
    }
  }

  render() {
    const { isConnected, connectionStatus } = this.state;
    const { terminalId } = this.props;

    // 简化渲染日志 - 只在状态变化时输出
    if (this.lastLoggedState !== `${connectionStatus}-${terminalId}`) {
      console.log(`🎨 Instance ${this.instanceId}: ${connectionStatus}, terminal: ${terminalId?.slice(-8)}`);
      this.lastLoggedState = `${connectionStatus}-${terminalId}`;
    }

    return (
      <div className="flex flex-col h-full">
        {/* Connection Status Bar */}
        <div className="flex items-center justify-between px-3 py-2 bg-gray-100 dark:bg-gray-800 border-b">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className={`text-sm font-medium ${this.getStatusColor()}`}>
              {this.getStatusText()} (Instance {this.instanceId})
            </span>
            {/* 重连按钮 - 只在连接失败或错误时显示 */}
            {(connectionStatus === 'failed' || connectionStatus === 'error') && (
              <button
                onClick={this.handleReconnect}
                className="ml-2 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                title="Reconnect to terminal service"
              >
                🔄 Reconnect
              </button>
            )}
          </div>
          <div className="text-xs text-gray-500">
            Terminal: {terminalId || 'New'}
          </div>
        </div>

        {/* Terminal Container */}
        <div
          ref={this.setTerminalRef}
          className="flex-1 p-2 bg-black"
          style={{ minHeight: '400px' }}
        />
      </div>
    );
  }
}

export default SocketIOTerminal;
